import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import type Team from '../../model/Team'
import { useModal } from '../../contexts/ModalContext'
import { useChallenge } from '../../hooks/challenge/useChallenge'
import { logError } from '../../util/errorLogger'
import AppIcon from '../icons/AppIcon'
import Overlay, { OverlayCard } from '../primitives/Overlay'
import styles from './TeamScreen.module.css'

function buildInviteText(challengeName: string) {
  return `Your friend is inviting you to join their team for the ${challengeName}. Hit the link to join.

Need more details? Check out: https://relentless2800.com`
}

interface TeamInviteModalProps {
  team: Team
  onClose: () => void
}

export default function TeamInviteModal({ team, onClose }: TeamInviteModalProps) {
  const { showSnackbar } = useModal()
  const { data: challenge } = useChallenge(team.challengeID)
  const challengeName = challenge?.name ?? 'challenge'
  const inviteText = buildInviteText(challengeName)

  async function handleShare() {
    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin
    const url = `${baseUrl}/team/${team.id}`

    try {
      if (Capacitor.getPlatform() === 'web' && navigator.share) {
        await navigator.share({
          title: 'Join my Relentless team!',
          text: inviteText,
          url,
        })
        return
      }

      if (Capacitor.getPlatform() !== 'web') {
        await Share.share({
          title: 'Join my Relentless team!',
          url,
          text: inviteText,
          dialogTitle: 'Share with your friends',
        })
        return
      }

      await navigator.clipboard.writeText(url)
      showSnackbar('invite-share', 'Invite link copied to clipboard', 'success')
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') return
      void logError(error, { component: 'TeamInviteModal', action: 'share' })
      showSnackbar('invite-share-error', 'Could not share invite link', 'error')
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(team.code)
      showSnackbar('copy-team-code', 'Team code copied!', 'success')
    } catch (error) {
      void logError(error, { component: 'TeamInviteModal', action: 'copyCode' })
      showSnackbar('copy-team-code-error', 'Failed to copy team code', 'error')
    }
  }

  return (
    <Overlay variant="backdrop" onClose={onClose}>
      <OverlayCard>
        <div className={styles.modalEyebrow}>Invite teammates</div>
        <div className={styles.modalTitle}>Grow the squad</div>
        <div className={styles.invCode}>
          <span>{team.code}</span>
        </div>
        <div className={styles.invBtns}>
          <button type="button" className={[styles.gbtn, styles.gbtnPrimary].join(' ')} onClick={handleCopy}>
            <AppIcon icon="mdi:content-copy" size={16} />
            Copy code
          </button>
          <button type="button" className={styles.gbtn} onClick={handleShare}>
            <AppIcon icon="mdi:share-variant" size={16} />
            Share link
          </button>
        </div>
        <button type="button" className={styles.modalDismiss} onClick={onClose}>
          Done
        </button>
      </OverlayCard>
    </Overlay>
  )
}
